/**
 * Detect stored email bodies that are HTML despite email_format=text (template vs body mismatch).
 * Used for analytics / activity expanded rows so we render with sanitizeEmailHTML instead of stripping tags.
 */
export function emailBodyLooksLikeHtml(body: string | null | undefined): boolean {
  if (!body || typeof body !== 'string') return false;
  return /<(p|div|br|span|a|table|td|tr|img|h[1-6]|strong|em|b|i|u|ul|ol|li|html|body)\b[^>]*>/i.test(body.trim());
}

/**
 * How mail clients introduce a quoted reply, in the languages prospects answer in. Jumio's APAC
 * and Hologram's LATAM threads arrive as Chinese ("发件人：… 日期：…", "…于 … 写道：") and
 * Spanish ("El … ha escrito:"); matching English alone left every earlier message of those
 * threads inline under the reply.
 *
 * The translated forms start with ordinary words ("El equipo escribió: …"), so one counts only
 * when the line ends at its colon and carries what every real attribution carries: the sender's
 * address or the time it was sent. English keeps its long-standing "On … wrote:" rule.
 */
const ENGLISH_ATTRIBUTION = /^On\s.+wrote:/;
const TRANSLATED_ATTRIBUTION = new RegExp(`^(?:${[
  'El\\s.{5,200}?(?:escribió|ha escrito)',
  'Em\\s.{5,200}?escreveu',
  'Le\\s.{5,200}?a\\s+écrit',
  'Am\\s.{5,200}?schrieb.{0,120}?',
  'Il\\s.{5,200}?ha\\s+scritto',
  'Op\\s.{5,200}?schreef.{0,120}?',
  '.{1,200}?于.{1,120}?写道',
  '.{1,200}?が書きました',
].join('|')})\\s*[:：]\\s*$`, 'i');
const ATTRIBUTION_EVIDENCE = /@|\d{1,2}:\d{2}/;

/** Whether a whole (trimmed) line is a translated reply attribution with its address or time. */
export function isTranslatedQuoteAttribution(line: string): boolean {
  return line.length <= 300 && TRANSLATED_ATTRIBUTION.test(line) && ATTRIBUTION_EVIDENCE.test(line);
}

/** Whether a whole (trimmed) line is a reply attribution, in English or a translation. */
const isQuoteAttributionLine = (line: string): boolean => (
  ENGLISH_ATTRIBUTION.test(line) || isTranslatedQuoteAttribution(line)
);

/** The "From:" label of a forwarded/quoted header block, and the "Sent:"/"Date:" label under it. */
export const QUOTE_FROM_LABEL = /^(?:From|De|Von|Van|Da|发件人|寄件者|差出人)\s*[:：]/i;
const QUOTE_SENT_LABEL = /^(?:Sent|Date|Enviado|Fecha|Enviada em|Data|Envoyé|Gesendet|Datum|Verzonden|Inviato|发送时间|日期|傳送時間|送信日時)\s*[:：]/i;
/**
 * The "Sent:" label inside a short header element (the HTML Outlook block). A Latin label must be
 * its own word — "Consent:" is not "Sent:" — while a CJK label follows a name with no space.
 */
export const QUOTE_SENT_LABEL_ANYWHERE = /(?:(?<![\p{L}\p{N}])(?:Sent|Enviado|Enviada em|Envoyé|Gesendet|Verzonden|Inviato)|发送时间|日期|傳送時間|送信日時)\s*[:：]/iu;

/** Outlook's attribution block: "From: …" with a "Sent:"/"Date:" line just below it, in any language above. */
const isOutlookAttribution = (lines: string[], i: number): boolean => {
  if (!QUOTE_FROM_LABEL.test(lines[i].trim())) return false;
  return lines.slice(i + 1, i + 5).some((l) => QUOTE_SENT_LABEL.test(l.trim()));
};

/**
 * Outlook's underscore rule above a quoted thread. Unlike a dashed separator this is
 * also a common signature divider, so it only counts as a boundary when a quote
 * header actually follows it.
 */
const isUnderscoreDivider = (lines: string[], i: number): boolean => {
  if (!/^_{3,}$/.test(lines[i].trim())) return false;
  const following = lines.slice(i + 1, i + 6).filter((l) => l.trim());
  return following.some((l, j) => isOutlookAttribution(following, j) || isQuoteAttributionLine(l.trim()));
};

/**
 * Strip quoted reply history from a PLAIN-TEXT email body.
 *
 * Needed because the mailbox scanner asks Microsoft Graph for
 * `Prefer: outlook.body-content-type="text"` and stores that plain text in
 * `email_body_html` — roughly a third of ingested rows across every tenant. Nothing
 * DOM-based can find a quote boundary in it: there are no `.gmail_quote` classes and
 * no `<b>From:</b>`, just lines. Run this before any HTML conversion or DOM parsing,
 * which also avoids DOMParser mangling bare addresses like `<user@domain.com>`.
 */
export function stripPlainTextQuotes(text: string): string {
  if (!text) return '';

  // Inline quoting: the whole reply arrives as one line with no breaks.
  const inlineMatch = text.match(/\s+>?\s*On\s.{5,80}wrote:/);
  if (inlineMatch && inlineMatch.index !== undefined) {
    return text.slice(0, inlineMatch.index).trim();
  }

  const lines = text.split(/\r?\n/);
  let cutAt = lines.length;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i].trim();
    if (
      line.startsWith('>')
      || isQuoteAttributionLine(line)
      || /^-{3,}/.test(line) // --- separator, incl. "-----Original Message-----"
      || isOutlookAttribution(lines, i)
      || isUnderscoreDivider(lines, i)
    ) {
      cutAt = i;
      break;
    }
  }
  return lines.slice(0, cutAt).join('\n').trim();
}

/**
 * Attachment placeholders Outlook writes into the plain-text alternative of a message:
 * `[image001.png]` for an inline image and `[cid:<content-id>]` for one referenced by
 * Content-ID. Both name a part the reader has no way to see, so in a scanned body they
 * are pure noise — a signature's logo strip arrives as four `[image.png]` tokens and a
 * booking button as a raw UUID.
 *
 * A placeholder is usually followed by the link it wrapped, in Outlook's `<url>`
 * notation; that link is dropped with it, because the label line underneath repeats it.
 * A line left empty is removed rather than left as a gap.
 */
export function stripInlineImagePlaceholders(text: string): string {
  if (!text) return '';

  const PLACEHOLDER = /\[(?:cid:[^\]\n]+|image[^\]\n]*\.(?:png|jpe?g|gif|bmp|webp))\][ \t]*(?:<[^>\s]*>)?/gi;

  // The pattern spans no newlines, so line count is preserved and a stripped line can be
  // compared with the one it came from: a line that is empty only because its tokens were
  // removed is deleted, while a blank line the author wrote is left alone.
  const original = text.split(/\r?\n/);
  const kept = original
    .map((line) => line.replace(PLACEHOLDER, ''))
    .filter((line, i) => line.trim() !== '' || original[i].trim() === '');

  return kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Outlook's plain-text alternative encodes hyperlinks as `label<https://url>` or
 * `label<mailto:addr>`. The bracketed URL is unreadable noise once rendered — keep the
 * visible label only. Bare RFC addresses (`Name <user@domain.com>`) are untouched because
 * they lack a `http(s):` or `mailto:` scheme inside the brackets.
 */
export function normalizeOutlookPlainTextLinks(text: string): string {
  if (!text) return '';

  return text
    .replace(/([^\s<\n][^<\n]*?)<https?:\/\/[^>\n]+>/gi, '$1')
    .replace(/([^\s<\n][^<\n]*?)<mailto:[^>\n]+>/gi, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+\|([ \t]*\|)+/g, ' |');
}

/** Salesforce/Outlook open-tracking beacons arrive as bracketed URLs in plain text. */
const isTrackingUrl = (url: string): boolean =>
  /\/t\.gif(?:[#?]|$)/i.test(url)
  || /salesforce\.com\/img\/clients\/mailapp/i.test(url)
  || (/\.(?:gif|png|jpe?g)(?:[#?]|$)/i.test(url) && /track|beacon|pixel|open\./i.test(url));

export function stripBracketedTrackingUrls(text: string): string {
  if (!text) return '';

  const BRACKETED_URL = /\[(https?:\/\/[^\]\n]+)\]/gi;
  const original = text.split(/\r?\n/);
  const kept = original
    .map((line) =>
      line.replace(BRACKETED_URL, (match, url: string) => (isTrackingUrl(url) ? '' : match)),
    )
    .filter((line, i) => line.trim() !== '' || original[i].trim() === '');

  return kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/** Full plain-text cleanup pipeline for scanned Outlook/Gmail bodies before HTML conversion. */
export function preparePlainTextEmailBody(text: string): string {
  return stripBracketedTrackingUrls(
    normalizeOutlookPlainTextLinks(stripInlineImagePlaceholders(text)),
  );
}
